import React, { useCallback, useEffect, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister } from '@lexical/utils'
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { ChevronRight } from '@mui/icons-material'
import { $getSelection, $isRangeSelection, TextNode } from 'lexical'
import {
  CitationNode,
  $citationForwardTransform,
  $citationReverseTransform,
  getCitationBody,
} from './citation-node'

let CITATION_PLACEHOLDER = '{cite: }'
// Cursor lands right before the closing "}" so the author can start typing the reference immediately
let CITATION_CURSOR_OFFSET = 1

interface CitationPluginProps {
  existingCitations?: string[]
}

export function CitationPlugin({
  existingCitations = [],
}: CitationPluginProps): React.JSX.Element {
  let [editor] = useLexicalComposerContext()
  let [menuPosition, setMenuPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  let [chooseExistingAnchor, setChooseExistingAnchor] =
    useState<HTMLElement | null>(null)

  useEffect(
    () =>
      mergeRegister(
        editor.registerNodeTransform(TextNode, $citationForwardTransform),
        editor.registerNodeTransform(CitationNode, $citationReverseTransform),
      ),
    [editor],
  )

  let handleContextMenu = useCallback(
    (event: MouseEvent) => {
      let rootElement = editor.getRootElement()
      if (!rootElement?.contains(event.target as Node)) return

      event.preventDefault()
      setMenuPosition({ top: event.clientY, left: event.clientX })
      setChooseExistingAnchor(null)
    },
    [editor],
  )

  useEffect(() => {
    let rootElement = editor.getRootElement()
    rootElement?.addEventListener('contextmenu', handleContextMenu)
    return () => {
      rootElement?.removeEventListener('contextmenu', handleContextMenu)
    }
  }, [editor, handleContextMenu])

  let handleClose = () => {
    setMenuPosition(null)
    setChooseExistingAnchor(null)
  }

  let handleInsertCitation = () => {
    handleClose()
    editor.update(() => {
      let selection = $getSelection()
      if (!$isRangeSelection(selection)) return

      selection.insertText(CITATION_PLACEHOLDER)

      // Move the cursor back before the closing brace
      let { anchor } = selection
      anchor.set(anchor.key, anchor.offset - CITATION_CURSOR_OFFSET, 'text')
      selection.focus.set(anchor.key, anchor.offset, 'text')
    })
    editor.focus()
  }

  let handleInsertExistingCitation = (citationToken: string) => {
    handleClose()
    editor.update(() => {
      let selection = $getSelection()
      if (!$isRangeSelection(selection)) return

      selection.insertText(citationToken)
    })
    editor.focus()
  }

  return (
    <>
      <Menu
        open={menuPosition !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition ?? undefined}
      >
        <MenuItem onClick={handleInsertCitation}>Insert citation</MenuItem>
        <MenuItem
          disabled={existingCitations.length === 0}
          onClick={(event) => setChooseExistingAnchor(event.currentTarget)}
        >
          <ListItemText>Choose existing</ListItemText>
          <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
            <ChevronRight fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      </Menu>
      <Menu
        open={chooseExistingAnchor !== null}
        onClose={handleClose}
        anchorEl={chooseExistingAnchor}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {existingCitations.map((citationToken) => (
          <MenuItem
            key={citationToken}
            onClick={() => handleInsertExistingCitation(citationToken)}
          >
            {getCitationBody(citationToken)}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
